import { useState, type ReactElement, type ReactNode } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { ApiError } from '@kirin/api-client/errors';
import {
  DEVICE_ALERT_WINDOW_DAYS,
  DEVICE_PEOPLE_SEEN_DAYS,
  deviceIdSchema,
  type DeviceAiTool,
  type DeviceDetail,
  type DevicePerson,
  type DevicePersonIdentity,
  type DevicePersonRef,
} from '@kirin/api-client/contracts/device-inventory';
import { AgentVersionWithUpdate } from '@/components/shared/AgentVersionWithUpdate';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { queryKeys } from '@/hooks/api/queryKeys';
import { useIsOwnerOrManager } from '@/hooks/useRoleCheck';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { fetchDevice, fetchDeviceIdForEndpointId } from './api';
import {
  IdentityBadge,
  PersonAvatar,
  RevokedBadge,
  SharedBadge,
} from './badges';
import { DeviceRevocationActions, RevokedNotice } from './DeviceRevocation';
import { LinkPersonDialog } from './LinkPersonDialog';
import { SeverityPill } from './CellRenderers';
import { GUEST_LABEL, OS_FAMILY_LABELS } from './constants';
import { deviceDisplayName, deviceShownId, personLabel } from './deviceDisplay';
import {
  buildEndpointAlertsUrl,
  buildEndpointPagePath,
  buildEndpointsTabPath,
  buildPersonUsersPath,
} from './links';

/** Characters of the endpoint ID shown before "..." (Mockup 2). */
const ENDPOINT_ID_SHOWN = 16;

function BackToEndpoints({ orgId }: { orgId: string }): ReactElement {
  return (
    <Link
      to={buildEndpointsTabPath(orgId)}
      className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="size-4" />
      Back to Endpoints
    </Link>
  );
}

function PageShell({
  orgId,
  children,
}: {
  orgId: string;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="flex flex-col gap-6 p-6">
      <BackToEndpoints orgId={orgId} />
      {children}
    </div>
  );
}

/**
 * KIR-5412 (Mockups 2–3) — the endpoint page at
 * `/:orgId/inventory/endpoints/:deviceId`, for shared and single-user
 * machines: identity and posture, people seen in the last 30 days, the
 * machine's AI tools with who they were seen with, and alerts split by person.
 * Read-only for Support; Owners and Managers can link an Unattributed person
 * to a user (KIR-5413) and revoke or reset an enrolled machine (KIR-5658).
 */
export function EndpointPage(): ReactElement {
  const { organizationId, deviceId } = useParams<{
    organizationId: string;
    deviceId: string;
  }>();
  const orgId = organizationId ?? '';
  const id = deviceId ?? '';
  // A malformed ID is never a device: say "not found" without asking.
  const validId = deviceIdSchema.safeParse(id).success;

  const { data, dataUpdatedAt, isLoading, isError, error } = useQuery({
    queryKey: queryKeys.devices.detail(orgId, id),
    queryFn: () => fetchDevice(orgId, id),
    enabled: Boolean(orgId) && validId,
    staleTime: 30_000,
    retry: (count, err) =>
      !(err instanceof ApiError && err.status === 404) && count < 2,
  });

  if (validId && isLoading) {
    return (
      <PageShell orgId={orgId}>
        <div className="flex flex-col gap-3" aria-label="Loading endpoint">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </PageShell>
    );
  }
  if (!validId || isError || !data) {
    const notFound =
      !validId || (error instanceof ApiError && error.status === 404);
    return (
      <PageShell orgId={orgId}>
        <p role="alert" className="text-destructive">
          {notFound
            ? 'Endpoint not found. It may no longer report to Kirin.'
            : "Couldn't load this endpoint. Please try again."}
        </p>
      </PageShell>
    );
  }
  return (
    <EndpointDetailView orgId={orgId} device={data} fetchedAt={dataUpdatedAt} />
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-sm font-semibold">{children}</dd>
    </div>
  );
}

const SHOWN_ADDRESSES = 3;

/** Mockup 2: the first three MAC addresses, then "+N more" (all of them in its tooltip). */
function MacAddresses({ values }: { values: string[] }): ReactElement {
  if (values.length === 0) return <span>—</span>;
  const rest = values.length - SHOWN_ADDRESSES;
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-1 font-mono text-sm">
      {values.slice(0, SHOWN_ADDRESSES).map(v => (
        <span key={v}>{v}</span>
      ))}
      {rest > 0 && (
        <span
          className="text-muted-foreground"
          title={values.slice(SHOWN_ADDRESSES).join(', ')}
        >
          +{rest} more
        </span>
      )}
    </div>
  );
}

/** The first routable IP address (link-local hidden), with "+N" for the rest. */
function IpAddress({ values }: { values: string[] }): ReactElement {
  const routable = values.filter(ip => {
    const v = ip.trim().toLowerCase();
    return !/^fe[89ab][0-9a-f]:/.test(v) && !v.startsWith('169.254.');
  });
  if (routable.length === 0) return <>—</>;
  return (
    <span title={routable.join(', ')}>
      {routable[0]}
      {routable.length > 1 && (
        <span className="font-normal text-muted-foreground">
          {' '}
          +{routable.length - 1}
        </span>
      )}
    </span>
  );
}

function Card({
  title,
  label,
  children,
}: {
  title: string;
  label: string;
  children: ReactNode;
}): ReactElement {
  return (
    <section
      aria-label={label}
      className="flex min-w-0 flex-col gap-4 rounded-xl border bg-card p-5"
    >
      <h2 className="text-lg font-semibold">{title}</h2>
      {children}
    </section>
  );
}

/** A person's name, linking to their row on Inventory → Users when it lists them. */
function PersonName({
  orgId,
  person,
  label,
}: {
  orgId: string;
  person: DevicePersonRef & { identity: DevicePersonIdentity };
  label: string;
}): ReactElement {
  const href = buildPersonUsersPath(orgId, person);
  return href ? (
    <Link
      to={href}
      className="truncate text-foreground underline-offset-2 hover:underline"
    >
      {label}
    </Link>
  ) : (
    <span className="truncate">{label}</span>
  );
}

function EndpointDetailView({
  orgId,
  device,
  fetchedAt,
}: {
  orgId: string;
  device: DeviceDetail;
  /** The query's `dataUpdatedAt`: when this device was read. */
  fetchedAt: number;
}): ReactElement {
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.AGENT_AUTO_UPDATE,
    flagsmithFeatureFlags.SHARED_DEVICES,
  ]);
  const showUpdateState =
    flags[flagsmithFeatureFlags.AGENT_AUTO_UPDATE] ?? false;
  // KIR-5413: "Link to user" is a write, like the Users screen's: Owner or Manager.
  const { isOwnerOrManager } = useIsOwnerOrManager(orgId);
  const canLink =
    (flags[flagsmithFeatureFlags.SHARED_DEVICES] ?? false) && isOwnerOrManager;
  // KIR-5658: revoke / reset is the same Owner-or-Manager write.
  const canRevoke = canLink;
  const shownId = deviceShownId(device);

  return (
    <PageShell orgId={orgId}>
      <header className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-3xl font-bold">
            Endpoint: {deviceDisplayName(device)}
          </h1>
          <SharedBadge shared={device.shared} />
          {device.revokedAt && <RevokedBadge />}
          {canRevoke && (
            <div className="ml-auto">
              <DeviceRevocationActions organizationId={orgId} device={device} />
            </div>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          Physical machine view. Identity, posture, people, and alerts for this
          endpoint
        </p>
      </header>
      {device.revokedAt && <RevokedNotice revokedAt={device.revokedAt} />}

      <section
        aria-label="Identity and posture"
        className="flex flex-col gap-4 rounded-xl border bg-card p-5"
      >
        <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-6">
          <Field label="OS">
            {device.osString ?? OS_FAMILY_LABELS[device.osFamily]}
          </Field>
          <Field label="Agent version">
            <AgentVersionWithUpdate
              version={device.agentVersion}
              latestVersion={device.latestAgentVersion}
              updateState={showUpdateState ? device.updateState : null}
            />
          </Field>
          <Field label="Endpoint ID">
            {shownId ? (
              <span className="font-mono" title={shownId}>
                {shownId.length > ENDPOINT_ID_SHOWN
                  ? `${shownId.slice(0, ENDPOINT_ID_SHOWN)}...`
                  : shownId}
              </span>
            ) : (
              '—'
            )}
          </Field>
          <Field label="IP address">
            <IpAddress values={device.ipAddresses} />
          </Field>
          <Field label="Last seen">
            {device.lastSeenAt ? (
              <RelativeTimeWithTooltip date={device.lastSeenAt} />
            ) : (
              '—'
            )}
          </Field>
          <Field label={`People seen (${DEVICE_PEOPLE_SEEN_DAYS} days)`}>
            {device.peopleSeenCount}
          </Field>
        </dl>
        <div className="flex flex-col gap-1 border-t pt-4">
          <span className="text-sm text-muted-foreground">MAC addresses</span>
          <MacAddresses values={device.macAddresses} />
        </div>
      </section>

      <PeopleSection orgId={orgId} device={device} canLink={canLink} />
      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
        <AiToolsSection device={device} />
        <AlertsSection orgId={orgId} device={device} fetchedAt={fetchedAt} />
      </div>
    </PageShell>
  );
}

/** Rows a card shows before "Show all" (live testing: 50 alerts made a 4-screen page). */
export const CARD_PREVIEW_ROWS = 10;

/** The first {@link CARD_PREVIEW_ROWS} rows until the user expands the card. */
function usePreview<T>(rows: T[]): {
  shown: T[];
  toggle: ReactElement | null;
} {
  const [expanded, setExpanded] = useState(false);
  if (rows.length <= CARD_PREVIEW_ROWS) {
    return { shown: rows, toggle: null };
  }
  return {
    shown: expanded ? rows : rows.slice(0, CARD_PREVIEW_ROWS),
    toggle: (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="self-start"
        aria-expanded={expanded}
        onClick={() => setExpanded(v => !v)}
      >
        {expanded ? 'Show fewer' : `Show all ${rows.length}`}
      </Button>
    ),
  };
}

function EmptyRow({
  colSpan,
  children,
}: {
  colSpan: number;
  children: ReactNode;
}): ReactElement {
  return (
    <TableRow>
      <TableCell
        colSpan={colSpan}
        className="py-6 text-center text-muted-foreground"
      >
        {children}
      </TableCell>
    </TableRow>
  );
}

function PeopleSection({
  orgId,
  device,
  canLink,
}: {
  orgId: string;
  device: DeviceDetail;
  /** Owner or Manager with shared devices on: Unattributed rows (not Guest) get "Link to user". */
  canLink: boolean;
}): ReactElement {
  const [linking, setLinking] = useState<DevicePerson | null>(null);
  const only = !device.shared && device.people.length === 1;
  const columns = canLink ? 5 : 4;
  return (
    <Card
      label="People seen"
      title={`People seen on this endpoint (last ${DEVICE_PEOPLE_SEEN_DAYS} days)`}
    >
      <Table className="text-sm">
        <TableHeader>
          <TableRow>
            <TableHead>Person</TableHead>
            <TableHead>OS account</TableHead>
            <TableHead>Identity</TableHead>
            <TableHead>Last seen</TableHead>
            {canLink && (
              <TableHead>
                <span className="sr-only">Actions</span>
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {device.people.length === 0 ? (
            <EmptyRow colSpan={columns}>
              Nobody was seen on this endpoint in the last{' '}
              {DEVICE_PEOPLE_SEEN_DAYS} days.
            </EmptyRow>
          ) : (
            device.people.map(person => {
              const label = personLabel(person);
              return (
                <TableRow key={`${person.userId}-${person.machineId}`}>
                  <TableCell>
                    <div className="flex min-w-0 items-center gap-3">
                      <PersonAvatar
                        label={label}
                        seed={person.userId}
                        muted={person.identity === 'unattributed'}
                      />
                      <div className="flex min-w-0 flex-col">
                        <PersonName
                          orgId={orgId}
                          person={person}
                          label={label}
                        />
                        <span className="truncate text-xs text-muted-foreground">
                          {person.email ?? 'no email found'}
                        </span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{person.osAccount ?? '—'}</TableCell>
                  <TableCell>
                    <IdentityBadge
                      identity={person.identity}
                      guest={person.guest}
                    />
                  </TableCell>
                  <TableCell>
                    {person.lastSeenAt ? (
                      <RelativeTimeWithTooltip date={person.lastSeenAt} />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  {canLink && (
                    <TableCell className="text-right">
                      {/* KIR-5670: a Guest / kiosk link would be undone by the next such sign-in. */}
                      {person.identity === 'unattributed' && !person.guest && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          aria-label={`Link ${label} to user`}
                          onClick={() => setLinking(person)}
                        >
                          Link to user
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      {only && (
        <p
          role="note"
          className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-200"
        >
          This machine is used by one person. All activity on it is attributed
          to {personLabel(device.people[0])}.
        </p>
      )}
      {canLink && (
        <LinkPersonDialog
          organizationId={orgId}
          person={linking}
          onClose={() => setLinking(null)}
        />
      )}
    </Card>
  );
}

function toolKey(tool: DeviceAiTool): string {
  return `${tool.toolId}-${tool.category}`;
}

function AiToolsSection({ device }: { device: DeviceDetail }): ReactElement {
  const shadowAiOff = device.aiToolCount === null;
  const osAccounts = new Map(
    device.people.map(p => [p.userId, p.osAccount] as const)
  );
  const tools = usePreview(device.aiTools);
  return (
    <Card
      label="AI tools"
      title={device.shared ? 'Machine wide AI tools' : 'AI tools'}
    >
      <Table className="text-sm">
        <TableHeader>
          <TableRow>
            <TableHead>Tool</TableHead>
            <TableHead>Seen with</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {shadowAiOff ? (
            <EmptyRow colSpan={2}>
              Shadow AI is off for your organization, so Kirin does not list AI
              tools.
            </EmptyRow>
          ) : device.aiTools.length === 0 ? (
            <EmptyRow colSpan={2}>
              No AI tools were found on this endpoint.
            </EmptyRow>
          ) : (
            tools.shown.map(tool => (
              <TableRow key={toolKey(tool)}>
                <TableCell>{tool.name}</TableCell>
                <TableCell>
                  {tool.seenWith.length === 0
                    ? '—'
                    : tool.seenWith
                        .map(person =>
                          personLabel({
                            ...person,
                            osAccount: osAccounts.get(person.userId),
                          })
                        )
                        .join(', ')}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {!shadowAiOff && tools.toggle}
    </Card>
  );
}

/**
 * `fetchedAt` is when the counts were read, so the 7-day window in each link
 * matches the rows shown however long the page stays open.
 */
function AlertsSection({
  orgId,
  device,
  fetchedAt: now,
}: {
  orgId: string;
  device: DeviceDetail;
  fetchedAt: number;
}): ReactElement {
  // The Alerts page drops the endpoint filter without endpoint_identifier, so
  // endpoint-scoped links would open every alert in the org: show plain text.
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.ENDPOINT_IDENTIFIER,
  ]);
  const linkToAlerts =
    flags[flagsmithFeatureFlags.ENDPOINT_IDENTIFIER] ?? false;
  const peopleById = new Map(device.people.map(p => [p.userId, p] as const));
  const allAlertsHref = buildEndpointAlertsUrl(orgId, device.machineIds, now);
  const alerts = usePreview(device.alerts);
  return (
    <Card
      label="Alerts on this endpoint"
      title={
        device.shared
          ? 'Alerts on this endpoint, split by person'
          : 'Alerts on this endpoint'
      }
    >
      <Table className="text-sm">
        <TableHeader>
          <TableRow>
            <TableHead>Alert</TableHead>
            <TableHead>Person</TableHead>
            <TableHead>Severity</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {device.alerts.length === 0 ? (
            <EmptyRow colSpan={3}>
              No alerts on this endpoint in the last {DEVICE_ALERT_WINDOW_DAYS}{' '}
              days.
            </EmptyRow>
          ) : (
            alerts.shown.map(alert => {
              const seen = peopleById.get(alert.person.userId);
              const label = personLabel({
                ...alert.person,
                osAccount: seen?.osAccount,
              });
              const unattributed = seen?.identity === 'unattributed';
              const identityLabel = seen?.guest ? GUEST_LABEL : 'Unattributed';
              return (
                <TableRow
                  key={`${alert.findingKey}-${alert.person.userId}-${alert.machineId}`}
                >
                  <TableCell>
                    {linkToAlerts ? (
                      <Link
                        to={buildEndpointAlertsUrl(
                          orgId,
                          [alert.machineId],
                          now,
                          {
                            userId: alert.person.userId,
                            alert,
                          }
                        )}
                        className="text-foreground underline-offset-2 hover:underline"
                      >
                        {alert.title}
                      </Link>
                    ) : (
                      alert.title
                    )}
                  </TableCell>
                  <TableCell>
                    {unattributed ? `${label} (${identityLabel})` : label}
                  </TableCell>
                  <TableCell>
                    <SeverityPill severity={alert.severity} />
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      {alerts.toggle}
      {linkToAlerts && device.alertCount > 0 && (
        <Link
          to={allAlertsHref}
          className="self-start text-sm text-primary underline-offset-2 hover:underline"
        >
          View all alerts on this endpoint
        </Link>
      )}
    </Card>
  );
}

/**
 * KIR-5412 — `/:orgId/inventory/endpoints/by-endpoint-id/:machineId`: opens
 * the endpoint page of the machine an endpoint ID belongs to. A person's
 * endpoints (Users → Endpoints dialog) link here.
 */
export function EndpointLookupRedirect(): ReactElement {
  const { organizationId, machineId } = useParams<{
    organizationId: string;
    machineId: string;
  }>();
  const orgId = organizationId ?? '';
  const endpointId = machineId ?? '';
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.devices.byEndpointId(orgId, endpointId),
    queryFn: () => fetchDeviceIdForEndpointId(orgId, endpointId),
    enabled: Boolean(orgId && endpointId),
    staleTime: 30_000,
  });

  if (data) {
    return <Navigate to={buildEndpointPagePath(orgId, data)} replace />;
  }
  return (
    <PageShell orgId={orgId}>
      {isLoading ? (
        <p className="text-muted-foreground">Opening endpoint…</p>
      ) : (
        <p role="alert" className="text-destructive">
          {isError
            ? "Couldn't open this endpoint. Please try again."
            : 'Endpoint not found. It may no longer report to Kirin.'}
        </p>
      )}
    </PageShell>
  );
}
